/**
 * TherapySession — Ambient recording, dictation, and tap-and-check modes.
 *
 * UX Philosophy: "The record creates itself."
 * Maximum 2 taps to start recording. Zero training required.
 *
 * Mode 1: Ambient Recording (primary) — ONE BIG BUTTON
 * Mode 2: Post-Session Dictation (fallback)
 * Mode 3: Tap-and-Check (absolute minimum)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, Container, Divider,
  IconButton, LinearProgress, Paper, Slider, Stack, TextField,
  Tooltip, Typography, Alert, CircularProgress, Fade, Collapse,
} from '@mui/material';
import {
  FiberManualRecord as RecordIcon,
  Stop as StopIcon,
  Mic as MicIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  TouchApp as TapIcon,
  ArrowBack as BackIcon,
  Timer as TimerIcon,
  PlayArrow as PlayIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { EASI_DESIGN } from '../../theme/design-tokens';
import { therapy, therapyGoals, type TherapyGoal, type TherapyGoalTarget } from '../../api/endpoints';
import { useRecordingActivity } from '../../hooks/useRecordingActivity';

const d = EASI_DESIGN;

// ── IndexedDB fallback buffer for failed uploads ──────────────────
const IDB_NAME = 'easi-therapy-buffer';
const IDB_STORE = 'pending-uploads';
const IDB_VERSION = 1;

interface PendingUpload {
  id: string;
  patientId: string;
  blob: Blob;
  mimeType: string;
  sessionTimestamp: string;
  serviceType: string;
  durationMinutes: number;
  createdAt: string;
}

function openBufferDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function savePendingUpload(upload: PendingUpload): Promise<void> {
  const db = await openBufferDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(upload);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getPendingUploads(): Promise<PendingUpload[]> {
  const db = await openBufferDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function removePendingUpload(id: string): Promise<void> {
  const db = await openBufferDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function retryPendingUpload(upload: PendingUpload): Promise<boolean> {
  try {
    const urlRes = await therapy.getUploadUrl(upload.patientId, upload.mimeType);
    const urlData = (urlRes?.data as any)?.data;
    if (!urlData?.uploadUrl) return false;

    await fetch(urlData.uploadUrl, {
      method: 'PUT',
      body: upload.blob,
      headers: { 'Content-Type': upload.mimeType },
    });

    await therapy.createNote({
      patientId: upload.patientId,
      inputMode: 'ambient',
      sessionTimestamp: urlData.sessionTimestamp || upload.sessionTimestamp,
      sessionEnd: upload.createdAt,
      durationMinutes: upload.durationMinutes,
      recordingS3Key: urlData.s3Key,
      serviceType: upload.serviceType,
    });

    await removePendingUpload(upload.id);
    return true;
  } catch {
    return false;
  }
}

type InputMode = 'ambient' | 'dictation' | 'tap_check' | 'telehealth_import';
type SessionPhase = 'select_mode' | 'recording' | 'dictating' | 'tapping' | 'importing' | 'reviewing_import' | 'processing' | 'review';

interface PatientInfo {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  age?: number;
}

interface TherapySessionProps {
  patientId: string;
  patientName: string;
  patientInfo?: PatientInfo;
  onComplete?: () => void;
  onBack?: () => void;
}

export default function TherapySession({ patientId, patientName, patientInfo, onComplete, onBack }: TherapySessionProps) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<SessionPhase>('select_mode');
  const [inputMode, setInputMode] = useState<InputMode>('ambient');
  const [error, setError] = useState('');

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  useRecordingActivity(isRecording);
  const animFrameRef = useRef<number>(0);

  // Session data
  const [s3Key, setS3Key] = useState('');
  const [sessionTimestamp, setSessionTimestamp] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [soapNote, setSoapNote] = useState({ subjective: '', objective: '', assessment: '', plan: '' });

  // Dictation state
  const [dictationText, setDictationText] = useState('');

  // Tap-and-check state
  const [goals, setGoals] = useState<TherapyGoal[]>([]);
  const [goalEntries, setGoalEntries] = useState<TherapyGoalTarget[]>([]);
  const [serviceType, setServiceType] = useState('individual');
  const [durationMinutes, setDurationMinutes] = useState(30);

  // Processing state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Telehealth import state
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importFileContent, setImportFileContent] = useState('');
  const [importFileType, setImportFileType] = useState('txt');
  const [importDate, setImportDate] = useState(new Date().toISOString().slice(0, 10));
  const [importSoapData, setImportSoapData] = useState<any>(null);
  const [importRawTranscript, setImportRawTranscript] = useState('');
  const [importError, setImportError] = useState('');
  const [editableSoap, setEditableSoap] = useState({ subjective: '', objective: '', assessment: '', plan: '' });
  const [importGoalsAddressed, setImportGoalsAddressed] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Offline buffer state
  const [pendingCount, setPendingCount] = useState(0);
  const [retryingUploads, setRetryingUploads] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const soapPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load patient goals
  useEffect(() => {
    if (patientId) {
      therapyGoals.list(patientId, 'active').then(res => {
        const data = (res?.data as any)?.data || [];
        setGoals(data);
        setGoalEntries(data.map((g: TherapyGoal) => ({
          goalId: g.goalId,
          goalText: g.goalText,
          accuracyPct: null,
          cueingLevel: '',
          trials: null,
          notes: '',
        })));
      }).catch(() => {});
    }
  }, [patientId]);

  // ── OFFLINE BUFFER: check for pending uploads and retry ──────────
  useEffect(() => {
    const checkPending = async () => {
      try {
        const pending = await getPendingUploads();
        setPendingCount(pending.length);
      } catch { /* IndexedDB not available */ }
    };
    checkPending();

    // Retry pending uploads every 60 seconds
    retryTimerRef.current = setInterval(async () => {
      try {
        const pending = await getPendingUploads();
        if (pending.length === 0) return;
        setRetryingUploads(true);
        for (const upload of pending) {
          await retryPendingUpload(upload);
        }
        const remaining = await getPendingUploads();
        setPendingCount(remaining.length);
      } catch { /* retry next cycle */ }
      setRetryingUploads(false);
    }, 60_000);

    return () => {
      if (retryTimerRef.current) clearInterval(retryTimerRef.current);
      if (soapPollRef.current) clearInterval(soapPollRef.current);
    };
  }, []);

  // ── RECORDING ──────────────────────────────────────────

  const startRecording = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      // Audio level metering
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const updateLevel = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length / 255;
        setAudioLevel(avg);
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      // Determine codec
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;

      // Get upload URL
      const contentType = mimeType.split(';')[0];
      const urlRes = await therapy.getUploadUrl(patientId, contentType);
      const urlData = (urlRes?.data as any)?.data;
      if (urlData) {
        setS3Key(urlData.s3Key);
        setSessionTimestamp(urlData.sessionTimestamp);
      }

      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Could not access microphone. Please check permissions.');
    }
  }, [patientId]);

  const stopRecording = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    setIsRecording(false);

    return new Promise<Blob>((resolve) => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.onstop = () => {
          const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: mimeType });
          // Stop stream tracks
          streamRef.current?.getTracks().forEach(t => t.stop());
          resolve(blob);
        };
        mediaRecorderRef.current.stop();
      }
    });
  }, []);

  const handleStartSession = useCallback(async () => {
    setPhase('recording');
    await startRecording();
  }, [startRecording]);

  const handleEndSession = useCallback(async () => {
    setPhase('processing');
    const blob = await stopRecording();

    // Upload to S3
    if (s3Key) {
      try {
        const urlRes = await therapy.getUploadUrl(patientId, blob.type);
        const urlData = (urlRes?.data as any)?.data;
        if (urlData?.uploadUrl) {
          await fetch(urlData.uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': blob.type },
          });

          // Create therapy note (triggers async processing)
          await therapy.createNote({
            patientId,
            inputMode: 'ambient',
            sessionTimestamp: urlData.sessionTimestamp || sessionTimestamp,
            sessionEnd: new Date().toISOString(),
            durationMinutes: Math.ceil(recordingTime / 60),
            recordingS3Key: urlData.s3Key,
            serviceType,
          });

          setDraftNote('Your session is being processed. The draft note will appear in your inbox shortly.');
          setPhase('review');
        }
      } catch (err: any) {
        // Upload failed — store in IndexedDB as safety net
        try {
          const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          await savePendingUpload({
            id: pendingId,
            patientId,
            blob,
            mimeType: blob.type,
            sessionTimestamp: sessionTimestamp || new Date().toISOString(),
            serviceType,
            durationMinutes: Math.ceil(recordingTime / 60),
            createdAt: new Date().toISOString(),
          });
          const remaining = await getPendingUploads();
          setPendingCount(remaining.length);
          setDraftNote('Recording saved locally — will upload when connection is restored. You can close this page safely.');
          setPhase('review');
        } catch {
          // IndexedDB also failed — truly offline
          setError('Upload failed and local storage unavailable. Please try dictation mode to capture this session.');
          setPhase('select_mode');
        }
      }
    }
  }, [stopRecording, s3Key, patientId, sessionTimestamp, recordingTime, serviceType]);

  // ── DICTATION ──────────────────────────────────────────

  const handleSubmitDictation = useCallback(async () => {
    if (!dictationText.trim()) {
      setError('Please enter your session observations.');
      return;
    }
    setIsSubmitting(true);
    setPhase('processing');
    try {
      await therapy.createNote({
        patientId,
        inputMode: 'dictation',
        durationMinutes,
        serviceType,
        dictationText: dictationText.trim(),
        goalsTargeted: goalEntries.filter(g => g.accuracyPct !== null || g.cueingLevel),
      });
      setDraftNote('Your dictation has been submitted. The structured note will appear in your inbox shortly.');
      setPhase('review');
    } catch (err: any) {
      setError(err.message || 'Failed to submit dictation.');
      setPhase('dictating');
    }
    setIsSubmitting(false);
  }, [dictationText, patientId, durationMinutes, serviceType, goalEntries]);

  // ── TAP-AND-CHECK ──────────────────────────────────────

  const handleSubmitTapCheck = useCallback(async () => {
    const targeted = goalEntries.filter(g => g.accuracyPct !== null);
    if (targeted.length === 0) {
      setError('Please enter data for at least one goal.');
      return;
    }
    setIsSubmitting(true);
    setPhase('processing');
    try {
      await therapy.createNote({
        patientId,
        inputMode: 'tap_check',
        durationMinutes,
        serviceType,
        goalsTargeted: targeted,
      });
      setDraftNote('Tap-and-check note created and auto-approved.');
      setPhase('review');
    } catch (err: any) {
      setError(err.message || 'Failed to submit.');
      setPhase('tapping');
    }
    setIsSubmitting(false);
  }, [goalEntries, patientId, durationMinutes, serviceType]);

  // ── TELEHEALTH IMPORT ──────────────────────────────────
  const handleFileSelect = useCallback(async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large (max 5MB).');
      return;
    }
    setError('');
    setImportFile(file);
    setImportFileContent('');
    try {
      const { extractTranscriptText } = await import('../../utils/transcript-extraction');
      const { text, fileType } = await extractTranscriptText(file);
      if (!text.trim()) {
        setError('No readable text found in this file. Try copy-pasting the transcript instead.');
        return;
      }
      setImportFileType(fileType);
      setImportFileContent(text);
    } catch (err: any) {
      setError(err?.message || `Could not read ${file.name}. Try copy-pasting the transcript instead.`);
    }
  }, []);

  const handleImportDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleSubmitImport = useCallback(async () => {
    const content = importText.trim() || importFileContent.trim();
    if (!content) {
      setError('Please paste transcript text or upload a file.');
      return;
    }
    setIsSubmitting(true);
    setPhase('processing');
    setImportError('');
    try {
      // Build goals list from loaded goals
      const patientGoalsList = goals.map(g => ({ goalText: g.goalText }));

      // Compute patient age display
      let ageStr = '';
      if (patientInfo?.age != null && patientInfo.age > 0) {
        ageStr = `${Math.floor(patientInfo.age / 12)};${patientInfo.age % 12}`;
      }

      const res = await therapy.importTranscript({
        patientId,
        transcriptText: importText.trim() || undefined,
        fileContent: importFileContent.trim() || undefined,
        fileType: importFileType,
        sessionDate: importDate,
        patientName: patientInfo ? `${patientInfo.firstName} ${patientInfo.lastName}` : patientName,
        patientAge: ageStr,
        patientGoals: patientGoalsList,
      });

      const data = (res?.data as any)?.data || res?.data;
      const note = data?.note;
      const sessionTs = note?.session_timestamp || note?.sessionTimestamp;

      if (!sessionTs) {
        setError('Import failed — no session timestamp returned.');
        setPhase('importing');
        setIsSubmitting(false);
        return;
      }

      // Poll for SOAP completion — each poll fires easi-api-activity,
      // keeping the session alive (fixes session-expiry-during-processing bug).
      setImportRawTranscript(content);
      if (soapPollRef.current) clearInterval(soapPollRef.current);

      soapPollRef.current = setInterval(async () => {
        try {
          const noteRes = await therapy.getNote(patientId, sessionTs);
          const noteData = (noteRes?.data as any)?.data || noteRes?.data;
          const status = noteData?.processing_status || noteData?.processingStatus;

          if (status === 'completed') {
            if (soapPollRef.current) clearInterval(soapPollRef.current);
            soapPollRef.current = null;
            const soap = noteData?.soap_note || noteData?.soapNote || {};
            setImportSoapData({ soap });
            setEditableSoap({
              subjective: soap.subjective || '',
              objective: soap.objective || '',
              assessment: soap.assessment || '',
              plan: soap.plan || '',
            });
            const gt = noteData?.goals_targeted || noteData?.goalsTargeted || [];
            setImportGoalsAddressed(gt.map((g: any) => g.goalText || g.goal_text || '').filter(Boolean));
            setPhase('reviewing_import');
            setIsSubmitting(false);
          } else if (status === 'failed') {
            if (soapPollRef.current) clearInterval(soapPollRef.current);
            soapPollRef.current = null;
            const errMsg = noteData?.processing_error || noteData?.processingError || '';
            setImportSoapData(null);
            setEditableSoap({ subjective: '', objective: '', assessment: '', plan: '' });
            setImportGoalsAddressed([]);
            setImportError(errMsg || 'SOAP generation failed. You can write the note manually.');
            setPhase('reviewing_import');
            setIsSubmitting(false);
          }
          // else status === 'processing' — keep polling
        } catch {
          // Network blip — keep polling, don't abort
        }
      }, 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err.message || 'Import failed.';
      setError(msg);
      setPhase('importing');
      setIsSubmitting(false);
    }
  }, [importText, importFileContent, importFileType, importDate, patientId, patientInfo, patientName, goals]);

  const handleSaveImportDraft = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // The note was already created during import, now update the SOAP
      // Find the most recent note for this patient to update
      const notesRes = await therapy.listNotes(patientId, { limit: 1 });
      const notes = (notesRes?.data as any)?.data || notesRes?.data || [];
      const latestNote = Array.isArray(notes) ? notes[0] : null;
      if (latestNote) {
        const ts = latestNote.sessionTimestamp || latestNote.session_timestamp;
        await therapy.updateNote(patientId, ts, {
          soapNote: editableSoap,
        } as any);
      }
      setDraftNote('Telehealth note saved as draft. Review and approve in your inbox.');
      setPhase('review');
    } catch (err: any) {
      setError(err.message || 'Failed to save draft.');
    }
    setIsSubmitting(false);
  }, [patientId, editableSoap]);

  const handleApproveImport = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const notesRes = await therapy.listNotes(patientId, { limit: 1 });
      const notes = (notesRes?.data as any)?.data || notesRes?.data || [];
      const latestNote = Array.isArray(notes) ? notes[0] : null;
      if (latestNote) {
        const ts = latestNote.sessionTimestamp || latestNote.session_timestamp;
        await therapy.updateNote(patientId, ts, {
          soapNote: editableSoap,
        } as any);
        await therapy.approveNote(patientId, ts);
      }
      setDraftNote('Telehealth note approved and finalized.');
      setPhase('review');
    } catch (err: any) {
      setError(err.message || 'Failed to approve note.');
    }
    setIsSubmitting(false);
  }, [patientId, editableSoap]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── RENDER ──────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Pending uploads banner */}
      {pendingCount > 0 && (
        <Alert
          severity="warning"
          icon={retryingUploads ? <CircularProgress size={20} /> : <CloudOffIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          {retryingUploads
            ? 'Retrying upload...'
            : `${pendingCount} recording${pendingCount === 1 ? '' : 's'} saved locally — will upload when connection is restored.`
          }
        </Alert>
      )}

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <IconButton onClick={onBack || (() => navigate(-1))} size="small">
          <BackIcon />
        </IconButton>
        {patientInfo ? (
          <>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '50%',
                bgcolor: d.color.emerald, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 16, flexShrink: 0,
              }}
            >
              {patientInfo.firstName?.[0]}{patientInfo.lastName?.[0]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} color={d.color.emeraldDarkest} noWrap>
                Therapy Session — {patientInfo.firstName} {patientInfo.lastName}
                {patientInfo.age != null && patientInfo.age > 0 && (
                  <Typography component="span" variant="h6" fontWeight={600} color="text.secondary">
                    {' '}(Age {Math.floor(patientInfo.age / 12)};{patientInfo.age % 12})
                  </Typography>
                )}
              </Typography>
              {patientInfo.dateOfBirth && (
                <Typography variant="body2" color="text.secondary">
                  DOB: {patientInfo.dateOfBirth}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800} color={d.color.emeraldDarkest}>
              Therapy Session
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {patientName || 'Select a student'}
            </Typography>
          </Box>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ── MODE SELECTION ── */}
      {phase === 'select_mode' && (
        <Fade in>
          <Stack spacing={3}>
            {/* THE BIG BUTTON — Mode 1: Ambient Recording */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                border: `2px solid ${d.color.emeraldBorder}`,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${d.color.emeraldBg} 0%, #FFFFFF 100%)`,
              }}
            >
              <Button
                onClick={handleStartSession}
                sx={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  bgcolor: d.color.emerald,
                  color: '#FFFFFF',
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: `0 8px 32px rgba(16, 185, 129, 0.35)`,
                  transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                  '&:hover': {
                    bgcolor: d.color.emeraldDark,
                    transform: 'scale(1.05)',
                    boxShadow: `0 12px 40px rgba(16, 185, 129, 0.45)`,
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <MicIcon sx={{ fontSize: 48 }} />
                  <span>Start Session</span>
                </Stack>
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Records your session and generates notes automatically
              </Typography>
            </Paper>

            {/* Fallback modes */}
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => { setInputMode('dictation'); setPhase('dictating'); }}
                sx={{
                  py: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderColor: d.color.blue,
                  color: d.color.blue,
                  borderRadius: 2,
                  '&:hover': { bgcolor: d.color.blueBg },
                }}
              >
                Quick Note
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TapIcon />}
                onClick={() => { setInputMode('tap_check'); setPhase('tapping'); }}
                sx={{
                  py: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderColor: d.color.gold,
                  color: d.color.goldDark,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#FFFBEB' },
                }}
              >
                Tap & Check
              </Button>
            </Stack>

            {/* Telehealth Import */}
            <Tooltip title="Upload a transcript from a telehealth session. We will summarize it into a SOAP note and link it to this student's goals." arrow>
              <Paper
                elevation={0}
                onClick={() => { setInputMode('telehealth_import'); setPhase('importing'); }}
                sx={{
                  p: 2.5,
                  border: `1px solid ${d.color.slate200}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: d.shadow.cardHover,
                    borderColor: '#1E3A5F',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <CloudUploadIcon sx={{ fontSize: 32, color: '#1E3A5F' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={700} color="#1E3A5F">
                      Import Telehealth Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Drop your Zoom or Google Meet transcript here
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Tooltip>
          </Stack>
        </Fade>
      )}

      {/* ── RECORDING (Mode 1) ── */}
      {phase === 'recording' && (
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: `2px solid ${d.color.emerald}`,
              borderRadius: 4,
              background: '#FFFFFF',
            }}
          >
            {/* Pulsing recording indicator */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: '#EF4444',
                mx: 'auto',
                mb: 2,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                },
              }}
            />

            <Typography variant="h2" fontWeight={800} sx={{ fontFamily: 'monospace', mb: 1 }}>
              {formatTime(recordingTime)}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Recording...
            </Typography>

            {/* Audio level meter */}
            <Box sx={{ mx: 'auto', width: '60%', mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={audioLevel * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: audioLevel > 0.5 ? d.color.emerald : audioLevel > 0.2 ? '#F59E0B' : '#94A3B8',
                    transition: 'transform 0.1s',
                  },
                }}
              />
            </Box>

            {/* STOP BUTTON */}
            <Button
              onClick={handleEndSession}
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: '#EF4444',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.35)',
                transition: 'all 200ms ease-out',
                '&:hover': {
                  bgcolor: '#DC2626',
                  transform: 'scale(1.05)',
                },
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              <Stack alignItems="center" spacing={0.5}>
                <StopIcon sx={{ fontSize: 40 }} />
                <span>End Session</span>
              </Stack>
            </Button>
          </Paper>
        </Fade>
      )}

      {/* ── DICTATION (Mode 2) ── */}
      {phase === 'dictating' && (
        <Fade in>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>Quick Note</Typography>
            <Typography variant="body2" color="text.secondary">
              Dictate or type your session observations. We'll structure them into a SOAP note.
            </Typography>

            <TextField
              multiline
              minRows={6}
              maxRows={12}
              fullWidth
              placeholder="Worked on /r/ in initial position at sentence level. Marcus achieved 75% accuracy with verbal cueing. He was engaged and motivated today. Plan to increase to conversational level next session..."
              value={dictationText}
              onChange={e => setDictationText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                },
              }}
            />

            {/* Duration & Service Type */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>Duration (min)</Typography>
                <Slider
                  value={durationMinutes}
                  onChange={(_, v) => setDurationMinutes(v as number)}
                  min={5} max={60} step={5}
                  valueLabelDisplay="auto"
                  sx={{ color: d.color.emerald }}
                />
              </Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {['individual', 'group_2', 'group_3'].map(st => (
                  <Chip
                    key={st}
                    label={st === 'individual' ? 'Individual' : st === 'group_2' ? 'Group (2)' : 'Group (3)'}
                    onClick={() => setServiceType(st)}
                    color={serviceType === st ? 'primary' : 'default'}
                    variant={serviceType === st ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitDictation}
              disabled={isSubmitting || !dictationText.trim()}
              sx={{
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: d.color.emerald,
                borderRadius: 2,
                '&:hover': { bgcolor: d.color.emeraldDark },
              }}
            >
              Submit Note
            </Button>
          </Stack>
        </Fade>
      )}

      {/* ── TAP-AND-CHECK (Mode 3) ── */}
      {phase === 'tapping' && (
        <Fade in>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>Tap & Check</Typography>
            <Typography variant="body2" color="text.secondary">
              Select goals targeted, set accuracy, and tap cueing level.
            </Typography>

            {/* Duration & Service Type */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip icon={<TimerIcon />} label={`${durationMinutes} min`} size="small" />
              <Slider
                value={durationMinutes}
                onChange={(_, v) => setDurationMinutes(v as number)}
                min={5} max={60} step={5}
                sx={{ color: d.color.emerald, flex: 1 }}
              />
              <Stack direction="row" spacing={0.5}>
                {['individual', 'group_2', 'group_3'].map(st => (
                  <Chip
                    key={st}
                    label={st === 'individual' ? '1:1' : st === 'group_2' ? '1:2' : '1:3'}
                    onClick={() => setServiceType(st)}
                    color={serviceType === st ? 'primary' : 'default'}
                    variant={serviceType === st ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>

            {/* Goal entries */}
            {goalEntries.length === 0 && (
              <Alert severity="info">
                No active therapy goals found. Sync goals from IEP or create goals first.
              </Alert>
            )}

            {goalEntries.map((entry, idx) => (
              <Card key={entry.goalId} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                    {entry.goalText || `Goal ${idx + 1}`}
                  </Typography>

                  {/* Accuracy slider */}
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      Accuracy
                    </Typography>
                    <Slider
                      value={entry.accuracyPct ?? 0}
                      onChange={(_, v) => {
                        const updated = [...goalEntries];
                        updated[idx] = { ...updated[idx], accuracyPct: v as number };
                        setGoalEntries(updated);
                      }}
                      min={0} max={100} step={5}
                      valueLabelDisplay="auto"
                      valueLabelFormat={v => `${v}%`}
                      sx={{ color: d.color.emerald, flex: 1 }}
                    />
                    <Typography variant="body2" fontWeight={700} sx={{ minWidth: 40 }}>
                      {entry.accuracyPct !== null ? `${entry.accuracyPct}%` : '--'}
                    </Typography>
                  </Stack>

                  {/* Cueing level chips */}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                    {['independent', 'visual', 'verbal', 'model', 'physical'].map(level => (
                      <Chip
                        key={level}
                        label={level.charAt(0).toUpperCase() + level.slice(1)}
                        onClick={() => {
                          const updated = [...goalEntries];
                          updated[idx] = { ...updated[idx], cueingLevel: level };
                          setGoalEntries(updated);
                        }}
                        color={entry.cueingLevel === level ? 'primary' : 'default'}
                        variant={entry.cueingLevel === level ? 'filled' : 'outlined'}
                        size="small"
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitTapCheck}
              disabled={isSubmitting || goalEntries.every(g => g.accuracyPct === null)}
              sx={{
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: d.color.emerald,
                borderRadius: 2,
                '&:hover': { bgcolor: d.color.emeraldDark },
              }}
            >
              Submit Data
            </Button>
          </Stack>
        </Fade>
      )}

      {/* ── TELEHEALTH IMPORT (Mode 4) ── */}
      {phase === 'importing' && (
        <Fade in>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>Import Telehealth Note</Typography>
            <Typography variant="body2" color="text.secondary">
              {patientInfo ? `For ${patientInfo.firstName} ${patientInfo.lastName}` : patientName ? `For ${patientName}` : ''}
              {' — '}Paste a transcript, or drop a PDF, Word (.docx), .txt, .rtf, .vtt, or .srt file below.
            </Typography>

            {/* Date picker */}
            <TextField
              label="Session Date"
              type="date"
              value={importDate}
              onChange={e => setImportDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 200 }}
            />

            {/* File dropzone */}
            <Paper
              elevation={0}
              onDragOver={e => e.preventDefault()}
              onDrop={handleImportDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                p: 3,
                textAlign: 'center',
                border: `2px dashed ${importFile ? d.color.emerald : d.color.slate300}`,
                borderRadius: 2,
                bgcolor: importFile ? d.color.emeraldBg : d.color.slate50,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: d.color.emerald, bgcolor: d.color.emeraldBg },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.vtt,.srt,.rtf,.docx,.pdf,.doc"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
              <CloudUploadIcon sx={{ fontSize: 40, color: importFile ? d.color.emerald : d.color.slate400, mb: 1 }} />
              {importFile ? (
                <Typography variant="body2" fontWeight={600} color={d.color.emeraldDarkest}>
                  {importFile.name} ({(importFile.size / 1024).toFixed(0)} KB)
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Drop a .pdf, .docx, .txt, .rtf, .vtt, or .srt file here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to browse
                  </Typography>
                </>
              )}
            </Paper>

            {/* Copy-paste area */}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              — OR paste transcript text directly —
            </Typography>
            <TextField
              multiline
              minRows={6}
              maxRows={14}
              fullWidth
              placeholder="Paste your Zoom/Google Meet transcript here..."
              value={importText}
              onChange={e => setImportText(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.95rem' } }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitImport}
              disabled={isSubmitting || (!importText.trim() && !importFileContent.trim())}
              sx={{
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: '#1E3A5F',
                borderRadius: 2,
                '&:hover': { bgcolor: '#162D4A' },
              }}
            >
              {isSubmitting ? 'Processing...' : 'Import & Generate SOAP'}
            </Button>

            <Button
              variant="text"
              onClick={() => { setPhase('select_mode'); setImportText(''); setImportFile(null); setImportFileContent(''); }}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Back
            </Button>
          </Stack>
        </Fade>
      )}

      {/* ── REVIEWING IMPORT ── */}
      {phase === 'reviewing_import' && (
        <Fade in>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>Review Telehealth Note</Typography>

            {importError && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {importError}
                <Button
                  size="small"
                  onClick={handleSubmitImport}
                  sx={{ ml: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Retry SOAP Generation
                </Button>
              </Alert>
            )}

            {/* Split-pane: Left = raw transcript, Right = SOAP */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {/* Left: Raw transcript */}
              <Paper elevation={0} sx={{ flex: 1, p: 2, border: `1px solid ${d.color.slate200}`, borderRadius: 2, maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  ORIGINAL TRANSCRIPT
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6 }}>
                  {importRawTranscript}
                </Typography>
              </Paper>

              {/* Right: Editable SOAP */}
              <Stack sx={{ flex: 1 }} spacing={1.5}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  SOAP NOTE (editable)
                </Typography>
                {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => (
                  <TextField
                    key={field}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    value={editableSoap[field]}
                    onChange={e => setEditableSoap(prev => ({ ...prev, [field]: e.target.value }))}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.9rem' } }}
                  />
                ))}
              </Stack>
            </Stack>

            {/* Goals Addressed */}
            {importGoalsAddressed.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  GOALS ADDRESSED
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {importGoalsAddressed.map((g, i) => (
                    <Chip key={i} label={g} size="small" sx={{ bgcolor: d.color.emeraldPale, color: d.color.emeraldDarkest, fontWeight: 600, fontSize: '0.75rem' }} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSaveImportDraft}
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: d.color.slate300,
                }}
              >
                Save Draft
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApproveImport}
                disabled={isSubmitting || (!editableSoap.subjective && !editableSoap.objective)}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: d.color.emerald,
                  borderRadius: 2,
                  '&:hover': { bgcolor: d.color.emeraldDark },
                }}
              >
                Approve & Finalize
              </Button>
            </Stack>
          </Stack>
        </Fade>
      )}

      {/* ── PROCESSING ── */}
      {phase === 'processing' && (
        <Fade in>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={64} sx={{ color: d.color.emerald, mb: 3 }} />
            <Typography variant="h6" fontWeight={700}>Processing your session...</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This usually takes 1-3 minutes for ambient recordings.
            </Typography>
          </Box>
        </Fade>
      )}

      {/* ── REVIEW ── */}
      {phase === 'review' && (
        <Fade in>
          <Stack spacing={2}>
            <Alert severity="success" icon={<CheckIcon />}>
              {draftNote || 'Session note created successfully.'}
            </Alert>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/therapy/inbox')}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: d.color.emerald,
                  borderRadius: 2,
                  '&:hover': { bgcolor: d.color.emeraldDark },
                }}
              >
                Go to Inbox
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { setPhase('select_mode'); setError(''); }}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                Record Another
              </Button>
            </Stack>
          </Stack>
        </Fade>
      )}
    </Container>
  );
}
